import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { IColourPickerPresetGroups } from "@common/ux/colour-picker/colour-picker.component";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";

@Component({
    selector: "adapt-team-display-configuration",
    standalone: false,
    templateUrl: "./team-display-configuration.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./team-display-configuration.component.scss",
})
export class TeamDisplayConfigurationComponent implements OnInit {
    public readonly ConfigureOrganisation = ConfigurationAuthService.ConfigureOrganisation;

    @Input({ required: true }) public entity!: Team | TeamTypeConfiguration;
    @Input() public disabled = false;
    @Output() public configureOrganisationGroupTypesClicked = new EventEmitter<void>();

    @Output() public teamTypeConfigurationChanged = new EventEmitter<TeamTypeConfiguration>();

    public iconColourPresetGroups?: IColourPickerPresetGroups;
    public backgroundColourPresetGroups?: IColourPickerPresetGroups;
    public dummyTeam?: Team;

    public get previewTeam(): Team {
        if (this.isTeam(this.entity)) {
            return this.entity;
        }

        if (!this.dummyTeam) {
            this.dummyTeam = new Team();
            this.dummyTeam.teamTypeConfiguration = this.entity;
            this.dummyTeam.name = this.entity.name;
        }

        return this.dummyTeam;
    }

    public get teamIcon() {
        if (this.isTeam(this.entity)) {
            return this.entity.extensions.getCustomData().icon;
        }

        return this.entity.icon ?? undefined;
    }

    public set teamIcon(value: string | undefined) {
        if (this.isTeam(this.entity)) {
            const customData = this.entity.extensions.getCustomData();
            customData.icon = value;
            this.entity.extensions.updateCustomData(customData);
        } else {
            this.entity.icon = value;
        }
    }

    public get teamIconColour() {
        if (this.isTeam(this.entity)) {
            return this.entity.extensions.getCustomData()?.iconColour;
        }

        return this.entity.iconColour;
    }

    public set teamIconColour(value: string | undefined) {
        if (this.isTeam(this.entity)) {
            const customData = this.entity.extensions.getCustomData();
            customData.iconColour = value;
            this.entity.extensions.updateCustomData(customData);
        } else {
            this.entity.iconColour = value;
        }
    }

    public get teamBackgroundColour() {
        if (this.isTeam(this.entity)) {
            return this.entity.extensions.getCustomData()?.backgroundColour ?? null;
        }

        return this.entity.backgroundColour ?? null;
    }

    public set teamBackgroundColour(value: string | undefined | null) {
        if (this.isTeam(this.entity)) {
            const customData = this.entity.extensions.getCustomData();
            customData.backgroundColour = value ?? undefined;
            this.entity.extensions.updateCustomData(customData);
        } else {
            this.entity.backgroundColour = value ?? undefined;
        }
    }

    public constructor(
        private teamsService: CommonTeamsService,
    ) {}

    public async ngOnInit() {
        this.iconColourPresetGroups = await this.teamsService.promiseToGetUsedTeamIconColourPresetGroup();
        this.backgroundColourPresetGroups = await this.teamsService.promiseToGetUsedTeamBackgroundColourPresetGroup();
    }

    public isTeam(entity: Team | TeamTypeConfiguration): entity is Team {
        return entity instanceof Team;
    }
}
